import { Component, Input, OnInit } from '@angular/core';
import { IComment } from '@shared/interfaces/comment.interface';
import { IPayload } from '@shared/interfaces/payload.interface';
import { IReference } from '@shared/interfaces/reference.interface';
import { Comment } from '@shared/models/comment.model';
import { CommentsService } from '@shared/services/comments.service';
import { CommonEnvironmentsService } from '@shared/services/environments.service';
import { LoggerService } from '@shared/services/logger.service';
import { ToastService } from '@shared/services/toast.service';
import jwtDecode from 'jwt-decode';

@Component({
    selector: 'app-sidebar-reference-comments',
    templateUrl: './sidebar.reference.comments.component.html',
    styleUrls: ['./sidebar.reference.comments.component.scss'],
    standalone: false
})
export class SidebarReferenceCommentsComponent implements OnInit {
  @Input() public reference: IReference;

  public isLoading = Boolean(true);
  public isSaving = Boolean(false);
  public comments: IComment[] = [];
  public newCommentContent = '';
  public editingCommentId: string | null = null;
  public editingContent = '';
  public currentRecruiterId: string;

  private readonly constructorName: string = String(this.constructor.name);

  constructor(
    private readonly _comments: CommentsService,
    private readonly _logger: LoggerService,
    private readonly _toast: ToastService,
    private readonly _commonEnvironments: CommonEnvironmentsService
  ) {}

  ngOnInit(): void {
    const token = this._commonEnvironments.getToken();
    const payload: IPayload = jwtDecode(token);
    this.currentRecruiterId = payload.recruiter_id;
    this.getComments();
  }

  public getComments(): void {
    const id = this.reference?.id;
    const url = `GET /references/${id}/comments`;
    this._comments.getCommentsForReference(id).subscribe(
      (res: IComment[]) => {
        this._logger.info(this.constructorName, url, res);
        this.comments = res;
        this.isLoading = false;
      },
      (err: any) => {
        this._logger.error(this.constructorName, url, err);
        this.isLoading = false;
      }
    );
  }

  public addComment(): void {
    if (!this.newCommentContent.trim()) {
      return;
    }

    this.isSaving = true;
    const comment = new Comment({
      content: this.newCommentContent,
      identifiable_type: 'Reference',
      identifiable_id: this.reference.id
    });

    this._comments.post(comment.apiData).subscribe(
      (res: IComment) => {
        this._logger.info(this.constructorName, 'POST /comments', res);
        this.comments.unshift(res);
        this.newCommentContent = '';
        this.isSaving = false;
        this._toast.success('Comment added');
      },
      (err: any) => {
        this._logger.error(this.constructorName, 'POST /comments', err);
        this.isSaving = false;
        this._toast.error('Failed to add comment');
      }
    );
  }

  public startEdit(comment: IComment): void {
    this.editingCommentId = comment.id;
    this.editingContent = comment.content;
  }

  public cancelEdit(): void {
    this.editingCommentId = null;
    this.editingContent = '';
  }

  public saveEdit(comment: IComment): void {
    if (!this.editingContent.trim()) {
      return;
    }

    this.isSaving = true;
    const updatedComment = new Comment({
      id: comment.id,
      content: this.editingContent,
      identifiable_type: comment.identifiable_type,
      identifiable_id: comment.identifiable_id
    });

    this._comments.patch(comment.id, updatedComment.apiData).subscribe(
      (res: IComment) => {
        this._logger.info(
          this.constructorName,
          `PATCH /comments/${comment.id}`,
          res
        );
        const index = this.comments.findIndex((c) => c.id === comment.id);
        if (index !== -1) {
          this.comments[index] = res;
        }
        this.cancelEdit();
        this.isSaving = false;
        this._toast.success('Comment updated');
      },
      (err: any) => {
        this._logger.error(
          this.constructorName,
          `PATCH /comments/${comment.id}`,
          err
        );
        this.isSaving = false;
        this._toast.error('Failed to update comment');
      }
    );
  }

  public deleteComment(comment: IComment): void {
    if (!confirm('Are you sure you want to delete this comment?')) {
      return;
    }

    this._comments.delete(comment.id).subscribe(
      () => {
        this._logger.info(
          this.constructorName,
          `DELETE /comments/${comment.id}`,
          {}
        );
        this.comments = this.comments.filter((c) => c.id !== comment.id);
        this._toast.success('Comment deleted');
      },
      (err: any) => {
        this._logger.error(
          this.constructorName,
          `DELETE /comments/${comment.id}`,
          err
        );
        this._toast.error('Failed to delete comment');
      }
    );
  }

  public isAuthor(comment: IComment): boolean {
    return comment.author?.id === this.currentRecruiterId;
  }

  public trackByFn(comment: IComment): string {
    return comment.id;
  }
}
